import type { BBox, OrientationOut } from "../../api/types";
import { bearingDeg, type LonLat } from "../geo";

/** Sens de la manœuvre AMI → ENI (docs/14 §2.14) : jumeau client de `generex_engine/orientation.py`, mêmes formules
 *  (vérifiées par le banc `__fixtures__/orientation.json`, tools/manoeuvre/frame_fixture.py). Points en [lon, lat].
 *
 *  - Plan local centré sur le pivot P : 111 320 m par degré, cos(lat P) ; rotation horaire (formule de
 *    `centac.rotate`, `rotateCoordinate` de l'iPad).
 *  - `fitOf(θ)` : emprise du rectangle du dispositif tourné dans l'azimut θ et décalage minimal qui la ramène dans la
 *    cible ; T(θ)(p) = P + décalage + R(θ − θt)(p − P) ; d'un sens à l'autre, M = T(dst) ∘ T(src)⁻¹ (`transformOf`).
 *  - `feasibility` : les 360 caps entiers possibles, avec la même règle que le serveur pour le dispositif, plus les
 *    points posés à la main, les graphiques et le fuseau retouché (le serveur accepte tout ce que le client montre
 *    possible).
 *  Aucune dépendance à l'interface : textes et traductions restent aux composants (sauf la lecture des saisies). */

export const M_PER_DEG = 111_320;
export const CONTENT_MARGIN_M = 500;
export const TEMPLATE_SNAP_DEG = 0.5;
/** Aimantation du geste (±3°) vers les directions de la rose, le sens par défaut, son inverse et le sens appliqué. */
export const SNAP_DEG = 3;
const EPS_M = 1e-6;
const R_M = 6_378_137; // `user_corridor.R_M` (élargissement des emprises en mètres)
/** Marge des sommets d'un fuseau retouché autour de son amorce tournée : 1 km côté serveur, moins 100 m de sûreté. */
export const SEED_MARGIN_M = 1000 - 100;

export interface Content {
  a0: number;
  a1: number;
  b0: number;
  b1: number;
}

export interface Fit {
  bearing: number;
  template: number;
  pivot: LonLat;
  /** Décalage [est, nord] en mètres, arrondi au centimètre. */
  shift: [number, number];
  fits: boolean;
  content: Content;
  target: BBox;
}

const RAD = Math.PI / 180;

function kLon(pivot: LonLat): number {
  return M_PER_DEG * Math.cos(pivot[1] * RAD);
}

/** (x vers l'est, y vers le nord) en mètres depuis le pivot. */
export function toPlane(p: LonLat, pivot: LonLat): [number, number] {
  return [(p[0] - pivot[0]) * kLon(pivot), (p[1] - pivot[1]) * M_PER_DEG];
}

export function fromPlane(x: number, y: number, pivot: LonLat): LonLat {
  return [pivot[0] + x / kLon(pivot), pivot[1] + y / M_PER_DEG];
}

function turn(x: number, y: number, deg: number): [number, number] {
  if (deg === 0) return [x, y];
  const a = deg * RAD;
  return [y * Math.sin(a) + x * Math.cos(a), y * Math.cos(a) - x * Math.sin(a)];
}

/** Rotation horaire de `deg` degrés autour du pivot, dans le plan local. */
export function rotate(p: LonLat, deg: number, pivot: LonLat): LonLat {
  if (deg === 0) return p;
  const [x, y] = toPlane(p, pivot);
  const [rx, ry] = turn(x, y, deg);
  return fromPlane(rx, ry, pivot);
}

function axes(deg: number): [[number, number], [number, number]] {
  const a = deg * RAD;
  return [
    [Math.sin(a), Math.cos(a)],
    [Math.cos(a), -Math.sin(a)],
  ];
}

/** Rectangle des points autour du pivot, le long de l'axe `templateDeg` (marge de 500 m). */
export function contentOf(points: readonly LonLat[], pivot: LonLat, templateDeg: number): Content {
  const [u, v] = axes(templateDeg);
  const as: number[] = [];
  const bs: number[] = [];
  for (const p of points) {
    const [x, y] = toPlane(p, pivot);
    as.push(x * u[0] + y * u[1]);
    bs.push(x * v[0] + y * v[1]);
  }
  if (!as.length) {
    as.push(0);
    bs.push(0);
  }
  const m = CONTENT_MARGIN_M;
  return { a0: Math.min(...as) - m, a1: Math.max(...as) + m, b0: Math.min(...bs) - m, b1: Math.max(...bs) + m };
}

function corners(content: Content, bearing: number): [number, number][] {
  const [u, v] = axes(bearing);
  const out: [number, number][] = [];
  for (const a of [content.a0, content.a1])
    for (const b of [content.b0, content.b1]) out.push([a * u[0] + b * v[0], a * u[1] + b * v[1]]);
  return out;
}

/** Emprise [ouest, sud, est, nord] du rectangle posé dans l'azimut `bearing`, sans décalage. */
export function contentBox(content: Content, pivot: LonLat, bearing: number): BBox {
  const c = corners(content, bearing);
  const xs = c.map((p) => p[0]);
  const ys = c.map((p) => p[1]);
  const [west, south] = fromPlane(Math.min(...xs), Math.min(...ys), pivot);
  const [east, north] = fromPlane(Math.max(...xs), Math.max(...ys), pivot);
  return [west, south, east, north];
}

/** `round(x, 2)` de Python pour un décalage (au centimètre), sans zéro négatif. */
function centimetres(x: number): number {
  return Math.round(x * 100) / 100 + 0;
}

/** Ajustement du contenu dans la cible pour l'azimut `bearing` : décalage minimal par axe ; un axe qui ne tient pas
 *  est centré sur la cible. */
export function fitOf(content: Content, pivot: LonLat, template: number, bearing: number, target: BBox): Fit {
  const c = corners(content, bearing);
  const xs = c.map((p) => p[0]);
  const ys = c.map((p) => p[1]);
  const [xLow, yLow] = toPlane([target[0], target[1]], pivot);
  const [xHigh, yHigh] = toPlane([target[2], target[3]], pivot);
  let fits = true;
  const shift: number[] = [];
  for (const [low, high, lo, hi] of [
    [xLow, xHigh, Math.min(...xs), Math.max(...xs)],
    [yLow, yHigh, Math.min(...ys), Math.max(...ys)],
  ] as const) {
    let move: number;
    if (hi - lo > high - low + EPS_M) {
      fits = false;
      move = (low + high) / 2 - (lo + hi) / 2;
    } else if (lo < low - EPS_M) move = low - lo;
    else if (hi > high + EPS_M) move = high - hi;
    else move = 0;
    shift.push(centimetres(move));
  }
  return { bearing, template, pivot, shift: [shift[0]!, shift[1]!], fits, content, target };
}

export function delta(f: Fit): number {
  return f.bearing - f.template;
}

/** Centre courant de l'ajustement : P + décalage (centre de la flèche sur la carte). */
export function centerOf(f: Fit): LonLat {
  return fromPlane(f.shift[0], f.shift[1], f.pivot);
}

/** T(θ)(p). */
export function place(p: LonLat, f: Fit): LonLat {
  const [x, y] = toPlane(p, f.pivot);
  const [rx, ry] = turn(x, y, delta(f));
  return fromPlane(rx + f.shift[0], ry + f.shift[1], f.pivot);
}

/** T(θ)⁻¹(q). */
export function unplace(q: LonLat, f: Fit): LonLat {
  const [x, y] = toPlane(q, f.pivot);
  const [rx, ry] = turn(x - f.shift[0], y - f.shift[1], -delta(f));
  return fromPlane(rx, ry, f.pivot);
}

/** M = T(dst) ∘ T(src)⁻¹ ; identité si les deux ajustements sont égaux. */
export function transformOf(src: Fit, dst: Fit): (p: LonLat) => LonLat {
  if (
    delta(src) === delta(dst) &&
    src.shift[0] === dst.shift[0] &&
    src.shift[1] === dst.shift[1] &&
    src.pivot[0] === dst.pivot[0] &&
    src.pivot[1] === dst.pivot[1]
  )
    return (p) => p;
  return (p) => place(unplace(p, src), dst);
}

/** Emprise élargie d'une fraction de ses côtés et d'une marge en mètres (`user_corridor.expand_box`). */
export function expandBox(box: BBox, { ratio = 0, marginM = 0 }: { ratio?: number; marginM?: number } = {}): BBox {
  const [west, south, east, north] = box;
  const dLon = (east - west) * ratio;
  const dLat = (north - south) * ratio;
  const mid = (south + north) / 2;
  const mLat = marginM / (R_M * RAD);
  const mLon = mLat / Math.max(Math.cos(mid * RAD), 1e-6);
  return [west - dLon - mLon, south - dLat - mLat, east + dLon + mLon, north + dLat + mLat];
}

export function boxOf(points: readonly LonLat[]): BBox | null {
  if (!points.length) return null;
  let [w, s, e, n] = [Infinity, Infinity, -Infinity, -Infinity];
  for (const [lon, lat] of points) {
    w = Math.min(w, lon);
    e = Math.max(e, lon);
    s = Math.min(s, lat);
    n = Math.max(n, lat);
  }
  return [w, s, e, n];
}

export function insideBox(box: BBox, p: LonLat): boolean {
  return p[0] >= box[0] && p[0] <= box[2] && p[1] >= box[1] && p[1] <= box[3];
}

// --- État publié par le serveur --------------------------------------------------------------------------------

/** Modèle de calcul tiré du bloc `corridor.orientation` d'un camp (null : terrain libre, CENTAC, sans état). */
export interface OrientationModel {
  content: Content;
  pivot: LonLat;
  template: number;
  target: BBox;
  grid: BBox;
  /** Ajustement appliqué (celui du sens par défaut si aucun sens n'est appliqué). */
  current: Fit;
  requested: number | null;
  applied: boolean;
}

export function orientationModel(block: OrientationOut | null | undefined): OrientationModel | null {
  if (!block || !block.content || !block.pivot || block.template_deg === null || !block.target || !block.grid) return null;
  const template = block.template_deg;
  const content = block.content;
  const pivot = block.pivot;
  const target = block.target;
  const current = fitOf(content, pivot, template, block.applied ? block.bearing_deg : template, target);
  return { content, pivot, template, target, grid: block.grid, current, requested: block.requested_deg, applied: block.applied };
}

/** Ajustement d'un azimut (null : sens par défaut). */
export function fitFor(model: OrientationModel, bearing: number | null): Fit {
  return fitOf(model.content, model.pivot, model.template, bearing ?? model.template, model.target);
}

/** Écart angulaire (0 à 180°). */
export function angleGap(a: number, b: number): number {
  return Math.abs(((((a - b + 180) % 360) + 360) % 360) - 180);
}

export function normalizeDeg(deg: number): number {
  const out = Math.round((((deg % 360) + 360) % 360) * 10) / 10;
  return out >= 360 ? 0 : out + 0;
}

/** Cap à moins de 0,5° du sens par défaut : calé exactement dessus (enregistré comme absent). */
export function snapToTemplate(bearing: number, template: number): number {
  return angleGap(bearing, template) < TEMPLATE_SNAP_DEG ? template : bearing;
}

/** Forme stockée d'un azimut visé (null : sens par défaut). */
export function storedOrientation(bearing: number, template: number): { v: 1; bearing_deg: number } | null {
  const snapped = snapToTemplate(normalizeDeg(bearing), template);
  return snapped === template ? null : { v: 1, bearing_deg: normalizeDeg(snapped) };
}

// --- Sens possibles ---------------------------------------------------------------------------------------------

export interface FeasibilityInput {
  /** Positions posées à la main (tous les temps, unités hors gabarit de l'ODB ennemi comprises). */
  handPoints?: readonly LonLat[];
  /** Points des graphiques tactiques (seulement s'ils tournent avec la manœuvre). */
  graphicPoints?: readonly LonLat[];
  /** Sommets du fuseau retouché (limites gauche et droite) et son amorce. */
  corridor?: readonly LonLat[];
  seed?: readonly LonLat[];
}

/** Cause qui rend un cap impossible (la première relevée, dans cet ordre). */
export type Blocker = "content" | "hand" | "graphics" | "corridor";

export interface Feasibility {
  /** 360 caps entiers. */
  possible: boolean[];
  /** Pour un cap impossible : ce qui sort du camp ; nombre d'unités ou de graphiques concernés. */
  reasons: (null | { blocker: Blocker; count: number })[];
}

/** Cause qui rend l'azimut `deg` impossible (null : possible), avec la même règle que `feasibility`. Le sens courant
 *  est toujours possible. */
export function verdict(model: OrientationModel, input: FeasibilityInput, deg: number): Feasibility["reasons"][number] {
  if (angleGap(deg, model.current.bearing) < 1e-9) return null;
  const f = fitFor(model, deg);
  if (!f.fits) return { blocker: "content", count: 0 };
  const m = transformOf(model.current, f);
  const handOut = (input.handPoints ?? []).filter((p) => !insideBox(model.grid, m(p))).length;
  if (handOut) return { blocker: "hand", count: handOut };
  const graphicsOut = (input.graphicPoints ?? []).filter((p) => !insideBox(model.grid, m(p))).length;
  if (graphicsOut) return { blocker: "graphics", count: graphicsOut };
  const vertices = input.corridor ?? [];
  if (vertices.length) {
    const seedBox = boxOf((input.seed ?? []).map(m));
    const zone = seedBox ? expandBox(seedBox, { marginM: SEED_MARGIN_M }) : null;
    const outside = vertices.filter((p) => {
      const q = m(p);
      return !insideBox(model.grid, q) && !(zone && insideBox(zone, q));
    }).length;
    if (outside) return { blocker: "corridor", count: outside };
  }
  return null;
}

/** Sens possibles pour les 360 caps entiers ; le cap courant l'est toujours. */
export function feasibility(model: OrientationModel, input: FeasibilityInput = {}): Feasibility {
  const possible: boolean[] = [];
  const reasons: Feasibility["reasons"] = [];
  for (let deg = 0; deg < 360; deg++) {
    const reason = verdict(model, input, deg);
    possible.push(reason === null);
    reasons.push(reason);
  }
  const here = Math.round(model.current.bearing) % 360;
  possible[here] = true;
  reasons[here] = null;
  return { possible, reasons };
}

/** Dimensions du dispositif dans l'azimut `deg` (ouest-est, nord-sud) et celles qu'offre le camp (cible), en
 *  mètres : explication d'un sens qui ne tient pas. */
export function spans(model: OrientationModel, deg: number): { ew: number; ns: number; campEw: number; campNs: number } {
  const c = corners(model.content, deg);
  const xs = c.map((p) => p[0]);
  const ys = c.map((p) => p[1]);
  const [x0, y0] = toPlane([model.target[0], model.target[1]], model.pivot);
  const [x1, y1] = toPlane([model.target[2], model.target[3]], model.pivot);
  return {
    ew: Math.max(...xs) - Math.min(...xs),
    ns: Math.max(...ys) - Math.min(...ys),
    campEw: x1 - x0,
    campNs: y1 - y0,
  };
}

/** Le cap (arrondi au degré) est-il possible ? */
export function isPossible(possible: readonly boolean[], bearing: number): boolean {
  return possible[Math.round(normalizeDeg(bearing)) % 360] ?? false;
}

/** Secteurs possibles [premier, dernier] dans le sens horaire ; [[0, 359]] si tous le sont, [] si aucun. */
export function arcs(possible: readonly boolean[]): [number, number][] {
  if (possible.every(Boolean)) return [[0, 359]];
  if (!possible.some(Boolean)) return [];
  const start = possible.findIndex((p) => !p);
  const out: [number, number][] = [];
  let current: number | null = null;
  for (let k = 1; k <= 360; k++) {
    const deg = (start + k) % 360;
    if (possible[deg] && current === null) current = deg;
    else if (!possible[deg] && current !== null) {
      out.push([current, (deg + 359) % 360]);
      current = null;
    }
  }
  return out;
}

/** Cap entier possible le plus proche (null si aucun). */
export function nearestPossible(possible: readonly boolean[], bearing: number): number | null {
  const from = Math.round(normalizeDeg(bearing)) % 360;
  for (let k = 0; k <= 180; k++) {
    for (const deg of [(from + k) % 360, (from - k + 360) % 360]) if (possible[deg]) return deg;
  }
  return null;
}

/** Aimantation à ±3° vers une des cibles ; rend le cap et la cible atteinte (null sans aimantation). */
export function snapBearing(raw: number, targets: readonly number[], off = false): { bearing: number; snapped: number | null } {
  const bearing = normalizeDeg(raw);
  if (off) return { bearing: Math.round(bearing) % 360, snapped: null };
  let best: number | null = null;
  for (const target of targets) {
    if (angleGap(bearing, target) <= SNAP_DEG && (best === null || angleGap(bearing, target) < angleGap(bearing, best)))
      best = target;
  }
  return best === null ? { bearing: Math.round(bearing) % 360, snapped: null } : { bearing: normalizeDeg(best), snapped: best };
}

/** Cap du pointeur depuis le centre (géographique, juste même quand la carte est tournée). */
export function pointerBearing(center: LonLat, point: LonLat): number {
  return bearingDeg(center, point);
}

// --- Lecture et écriture des caps -------------------------------------------------------------------------------

/** Rumbs des 16 aires de vent, en français et en anglais. */
export const RUMBS = {
  fr: ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSO", "SO", "OSO", "O", "ONO", "NO", "NNO"],
  en: ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"],
} as const;

/** Les huit directions de la rose, du nord dans le sens horaire. */
export const ROSE = [0, 45, 90, 135, 180, 225, 270, 315] as const;

const NAMES: Record<"fr" | "en", string[]> = {
  fr: ["nord", "nord-est", "est", "sud-est", "sud", "sud-ouest", "ouest", "nord-ouest"],
  en: ["north", "north-east", "east", "south-east", "south", "south-west", "west", "north-west"],
};

export function rumb16(deg: number, language: "fr" | "en" = "fr"): string {
  return RUMBS[language][Math.round(normalizeDeg(deg) / 22.5) % 16]!;
}

/** Nom de la direction de la rose la plus proche (« nord-est », « north-east »). */
export function directionName(deg: number, language: "fr" | "en" = "fr"): string {
  return NAMES[language][Math.round(normalizeDeg(deg) / 45) % 8]!;
}

/** Cap sur trois chiffres : « 045° ». */
export function formatBearing(deg: number): string {
  return `${String(Math.round(normalizeDeg(deg)) % 360).padStart(3, "0")}°`;
}

/** Millièmes (6 400 au tour), arrondis. */
export function toMils(deg: number): number {
  return Math.round((normalizeDeg(deg) * 6400) / 360) % 6400;
}

/** Saisie d'un azimut : « 45 », « 045 », « 45° », « 45,5 », « 800 mil », « 800 millièmes », « 800 mils », « NE »,
 *  « nord-est », « north-east » ; 360 → 0, −10 → 350, 6 400 mil → 0. null : saisie invalide (au-delà d'un tour). */
export function parseBearing(text: string): number | null {
  const raw = text.trim().toLowerCase().replace(/ | /g, " ");
  if (!raw) return null;
  const words = raw.replace(/[\s_]+/g, "-");
  for (const language of ["fr", "en"] as const) {
    const name = NAMES[language].indexOf(words);
    if (name >= 0) return ROSE[name]!;
    const rumb = (RUMBS[language] as readonly string[]).indexOf(raw.toUpperCase());
    if (rumb >= 0) return normalizeDeg(rumb * 22.5);
  }
  const found = /^([+-]?\d+(?:[.,]\d+)?)\s*(°|deg|degrés?|degrees?|mils?|millièmes?|milliemes?|‰)?$/.exec(raw.replace(/\s+/g, " "));
  if (!found) return null;
  const value = Number(found[1]!.replace(",", "."));
  if (!Number.isFinite(value)) return null;
  const unit = found[2] ?? "";
  const mils = /^(mil|‰)/.test(unit);
  if (mils) return Math.abs(value) > 6400 ? null : normalizeDeg((value * 360) / 6400);
  return Math.abs(value) > 360 ? null : normalizeDeg(value);
}

/** Direction (parmi les huit) d'un décalage [est, nord] en mètres, et sa longueur ; null sous 50 m. */
export function shiftDirection(shift: readonly [number, number]): { deg: number; metres: number } | null {
  const metres = Math.hypot(shift[0], shift[1]);
  if (metres < 50) return null;
  const deg = (Math.atan2(shift[0], shift[1]) * 180) / Math.PI;
  return { deg: ROSE[Math.round(normalizeDeg(deg) / 45) % 8]!, metres };
}
